<template>
  <div :class="['card-chart', { fullscreen: dashboardFullscreen }]" :style="{ height: cardHeight }">
    <CardInteractive
      :title="title"
      :info="info"
      :enableFullscreen="enableFullscreen"
      :showControls="fullscreen && enableResolution"
      :isLoading="isLoading"
      header-divider
      @toggleFullscreen="toggleFullscreen"
      @toggleShowInfo="(value) => (showingInfo = value)"
    >
      <template #controls>
        <UnderlineTabGroup
          tabStyle="pillTabSmall"
          size="small"
          :buttons="buttons"
          :selectedName="selectedName"
        />
      </template>
      <div
        :class="['chart-container', { fullscreen, checkboxes: showLargeCheckboxes }]"
        style="height: 100%; width: 100%"
      >
        <ChartBase
          ref="chartBase"
          :height="currentHeight"
          :components="components"
          :isFullscreen="fullscreen"
        />
        <div v-if="showLargeCheckboxes" class="controls-container">
          <div class="path-buttons">
            <div
              v-if="fullscreen"
              class="buttons select-all"
              @click="
                (e) => {
                  selectAll()
                  e.preventDefault()
                }
              "
            >
              <GraphCardCheckbox
                color="white"
                tintColor="white"
                checkedColor="blue"
                border-color="gray"
                :checked="isAllSelected"
              />
              <div class="buttons-label-container">
                <div class="buttons-label">{{ t('general__data__select-all') }}</div>
              </div>
            </div>
            <div
              v-for="btn in seriesCheckboxes"
              :key="btn.id"
              class="buttons"
              @click="
                (e) => {
                  handleSeriesSelection(btn.value)
                  e.preventDefault()
                }
              "
            >
              <GraphCardCheckbox
                :color="btn.color ?? 'white'"
                tintColor="white"
                :checkedColor="btn.color ?? 'blue'"
                :border-color="btn.color ?? 'gray'"
                :checked="isSeriesSelected(btn.value)"
              />
              <div class="buttons-label-container">
                <div class="buttons-label">{{ btn.text }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <template #header>
        <!-- Use seriesCheckboxes and handleSeriesSelection -->
        <!-- Adjust condition if needed, e.g., show max 4 checkboxes -->
        <div
          v-if="seriesCheckboxes.length > 0 && seriesCheckboxes.length <= 4 && !fullscreen"
          class="checkbox-row"
        >
          <div class="path-buttons-small-screen">
            <!-- Use btn.id for key -->
            <div
              v-for="btn in seriesCheckboxes"
              :key="btn.id"
              class="buttons"
              @click="
                (e) => {
                  handleSeriesSelection(btn.value)
                  e.preventDefault()
                }
              "
            >
              <GraphCardCheckbox
                :color="btn.color ?? 'white'"
                tintColor="white"
                :checkedColor="btn.color ?? 'blue'"
                :border-color="btn.color ?? 'gray'"
                :checked="isSeriesSelected(btn.value)"
              />
              <TextBase :text="btn.text" :type="'tiny'" :style="{ 'padding-top': '2px' }" />
            </div>
          </div>
        </div>
      </template>
      <template #footerControls>
        {{ t('dashboard__text__selection') }} :
        <CollapseControllerSummaryNew :showTimeSelection="true" :fullscreen="fullscreen" />
      </template>
    </CardInteractive>
  </div>
</template>

<script lang="ts" setup>
import UnderlineTabGroup from '@/design-system/molecules/UnderlineTabGroup.vue'
import { computed, inject, nextTick, ref, toRefs, watch } from 'vue'
import CardInteractive from './CardInteractive.vue'
import ChartBase from './ChartComponent/ChartBase.vue'
import { useI18n } from 'vue-i18n'
import { sharedDataKey } from '../../types'
import { useTimeSeries, TimeSeriesConfig } from './ChartComponent/chart/timeSeries'
import CollapseControllerSummaryNew from './CollapseController/CollapseControllerSummaryNew.vue'
import { ColorScaleConfig } from '@/utils/colorScale'
import { FormatOptions } from '../formatter'
import { useEntityLookup } from '@/composables/useEntityLookup'
import GraphCardCheckbox from './cards/GraphCardCheckbox.vue'
import TextBase from '@/design-system/atoms/TextBase.vue'

export type Props = {
  featureName: string
  title: string
  info?: string
  enableFullscreen?: boolean
  chartHeight?: string
  cardHeight?: string
  fields: string[]
  colorScale?: ColorScaleConfig
  dataFormat?: FormatOptions
}

const props = withDefaults(defineProps<Props>(), {
  chartHeight: '100%',
  cardHeight: '400px',
  colorScale: undefined,
  dataFormat: undefined,
  info: undefined
})
const sharedData = inject(sharedDataKey)
const dashboardFullscreen = computed(() => sharedData?.fullscreen ?? false)
const { chartHeight, colorScale, dataFormat } = toRefs(props)
const { getEntityDisplayStringFromPath } = useEntityLookup()
const { locale, t } = useI18n()

const showingInfo = ref(false)

const enableResolution = computed(() => true)
const chartBase = ref<InstanceType<typeof ChartBase> | undefined>(undefined)
const chart = computed<TimeSeriesConfig>((): TimeSeriesConfig => {
  return {
    dataSources: [],
    colorScale: colorScale?.value,
    dataFormat: dataFormat.value,
    missingDataMessage: {
      en: 'No data available',
      sv: 'Ingen data tillgänglig'
    }
  }
})

const selectedSeries = ref<SeriesIdentifier[]>([])
const {
  components,
  updateViewport,
  mergedData,
  measurements,
  isLoading,
  resolutionButtons: buttons,
  selectedResolution: selectedName
} = useTimeSeries(chartBase, chart, selectedSeries, props.featureName)

const showLargeCheckboxes = computed(() => fullscreen.value && seriesCheckboxes.value.length > 1)

type SeriesIdentifier = { path: string | null; field: string }
const hasValidPaths = computed<boolean>(() =>
  mergedData.value.some((d) => d.path !== null && d.path !== undefined)
)

const uniquePathsInData = computed<string[]>(() => {
  if (!hasValidPaths.value) return []
  return [...new Set(mergedData.value.map((d) => d.path).filter((path): path is string => !!path))]
})

const availableSeries = computed<SeriesIdentifier[]>(() => {
  if (hasValidPaths.value) {
    // Paths exist: Combine unique paths with measurement fields
    return uniquePathsInData.value.flatMap((path) =>
      measurements.value.map((m) => ({ path: path, field: m.field.name }))
    )
  } else if (mergedData.value.length > 0) {
    // No paths, but data exists: Series are defined by measurement fields only
    return measurements.value.map((m) => ({ path: null, field: m.field.name }))
  } else {
    // No data at all
    return []
  }
})

const isSingleFieldType = computed(() => measurements.value.length === 1)

const isSeriesSelected = (series: SeriesIdentifier): boolean => {
  return selectedSeries.value.some((s) => s.path === series.path && s.field === series.field)
}

const seriesCheckboxes = computed(() => {
  if (availableSeries.value.length <= 1) {
    // Hide checkboxes if only one series possible
    return []
  }

  return availableSeries.value
    .map((series) => {
      const measurement = measurements.value.find((m) => m.field.name === series.field)
      const measurementTitle = measurement?.title?.[locale.value] ?? series.field

      // Get the entity-specific part of the label using the helper
      const entityDisplayString = getEntityDisplayStringFromPath(series.path) // Call the new helper

      let displayText: string

      if (isSingleFieldType.value) {
        // Single field type: Use entity string if available, otherwise just the (single) measurement title
        displayText = entityDisplayString ?? measurementTitle // Use entity string or fallback to measurement title
      } else {
        // Multiple field types: Combine entity string and measurement title if entity string exists
        if (entityDisplayString) {
          displayText = `${entityDisplayString} - ${measurementTitle}`
        } else {
          // No path/entity string: Just use measurement title
          displayText = measurementTitle
        }
      }

      return {
        id: `${series.path ?? 'no_path'}|${series.field}`,
        text: displayText,
        value: series,
        path: series.path,
        field: series.field,
        color: measurement?.options?.color
      }
    })
    .toSorted((a, b) => a.text.localeCompare(b.text))
})

const handleSeriesSelection = (seriesToToggle: SeriesIdentifier) => {
  const index = selectedSeries.value.findIndex(
    (s) => s.path === seriesToToggle.path && s.field === seriesToToggle.field
  )
  if (index > -1) {
    selectedSeries.value.splice(index, 1)
  } else {
    selectedSeries.value.push(seriesToToggle)
  }
}

const isAllSelected = computed(() => {
  return seriesCheckboxes.value.every((checkbox) => isSeriesSelected(checkbox.value))
})

const selectAll = () => {
  if (isAllSelected.value) {
    selectedSeries.value = []
  } else {
    selectedSeries.value = [...availableSeries.value]
  }
}

const fullscreen = ref(false)

const currentHeight = computed(() => (fullscreen.value ? '600px' : chartHeight.value))

watch(
  availableSeries,
  (newAvailableSeries, oldAvailableSeries) => {
    if (newAvailableSeries.length === 0 && oldAvailableSeries && oldAvailableSeries.length > 0) {
      return
    }

    const preservedSelection = selectedSeries.value.filter((selected) =>
      newAvailableSeries.some(
        (available) => available.path === selected.path && available.field === selected.field
      )
    )

    if (preservedSelection.length === 0 && newAvailableSeries.length > 0) {
      selectedSeries.value = [...newAvailableSeries]
    } else {
      selectedSeries.value = preservedSelection
    }
  },
  { deep: true, immediate: true }
)

function toggleFullscreen(value: boolean) {
  fullscreen.value = value
  if (!value) {
    // Exiting fullscreen: select all available series
    selectedSeries.value = [...availableSeries.value]
  }
  nextTick(() => {
    updateViewport()
  })
}
</script>

<style scoped lang="scss">
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/mixins.scss';

.card-chart {
  width: 100%;
}

@media screen and (min-width: 768px) {
  .card-chart {
    width: 100%;

    &.fullscreen {
      min-height: 509px;
      width: calc(50% - variables.$gap-large);
    }
  }
}

.chart-container {
  height: 100%;
  display: grid;
  grid-template-columns: 1fr;

  &.checkboxes {
    grid-template-rows: 600px 1fr;

    @media screen and (min-width: 768px) {
      grid-template-columns: 1fr 200px;
      grid-template-rows: 1fr;
    }

    @media screen and (min-width: 1024px) {
      grid-template-columns: 1fr 300px;
      grid-template-rows: 1fr;
    }
  }
}

.checkbox-row {
  display: flex;
  gap: variables.$gap-xlarge;
  justify-content: left;
  flex-wrap: wrap;
  flex-direction: row;
}
.controls-container {
  display: flex;
  justify-content: flex-start;
  width: 100%;
  height: 100%;

  @media screen and (min-width: 768px) {
    overflow: hidden;
  }
}
.path-buttons {
  display: flex;
  flex-direction: column; /* Stack buttons vertically */
  gap: 10px;
  width: 100%;
  max-height: 600px;
  overflow-y: hidden;
  overflow-x: hidden;
  @include mixins.scrollbar-vertical;

  @media screen and (min-width: 768px) {
    overflow-y: auto; /* Enable vertical scrolling */
  }
}
.path-buttons label {
  display: flex;
  align-items: center;
  text-wrap: wrap;
  gap: 5px;
}
.buttons {
  display: flex;
  gap: 6px;
  padding-left: 2px;
  align-items: center;
  cursor: pointer;
  .buttons-label-container {
    overflow: hidden;
  }

  &.select-all {
    margin-bottom: 6px;
  }
}
.path-buttons-small-screen {
  display: flex;
  flex-direction: row;
  gap: 8px;
  overflow-x: auto;
  @include mixins.scrollbar-horizontal;
}
.buttons-label {
  @include font-mixins.desktop-regular-paragraph;
  color: black;
  overflow: hidden;
  white-space: nowrap;
  text-wrap: wrap;
}
</style>
